/**
 * หมัดหมอกวน (drift-fist): its lock routine (keys tj…) and its ท่าใหญ่ tf-supreme (keys tg…), design
 * docs/design/combat/2026-10-11-long-moves.md §1.3. Ideas only; names and choreography are ours.
 */
import { registerLongMove } from './registry';

/** 推 เมฆวนสองมือ — hands stick to the arm, draw the foe round twice, sink, lift, the double push. */
export const CLOUD_PULL = registerLongMove({
  skillId: 'tf-push',
  kind: 'lock',
  name: 'เมฆวนสองมือ',
  opener: { type: 'contact', range: 0, tell: 0 },
  len: 6.0,
  catch: 0.5,
  power: 2.4,
  lunge: 0.6,
  beats: [
    { at: 0, pose: 'tjStick', hits: 0, share: 0, foe: 'hit', fx: 'bindFlash', sfx: 'palmStick', cam: 'hold' },
    {
      at: 0.5,
      pose: 'tjDrawL',
      hits: 0,
      share: 0,
      foe: 'spin',
      fx: 'qiSwirl',
      sfx: 'clothWhirl',
      cam: 'orbit',
    },
    {
      at: 1.35,
      pose: 'tjDrawR',
      hits: 1,
      share: 0.1,
      foe: 'spin',
      fx: 'qiSwirl',
      sfx: 'palmTap',
      cam: 'orbit',
    },
    { at: 2.2, pose: 'tjSink', hits: 1, share: 0.14, foe: 'kneel', fx: 'groundRing', sfx: 'palmPress' },
    { at: 3.05, pose: 'tjLift', hits: 2, share: 0.16, foe: 'hit', fx: 'qiRise', sfx: 'palmTap2' },
    {
      at: 3.8,
      pose: 'tjGather',
      hits: 0,
      share: 0,
      foe: 'hit',
      fx: 'yinYangGather',
      sfx: 'qiGather',
      cam: 'push',
    },
    {
      at: 4.45,
      pose: 'tjPush',
      hits: 1,
      share: 0.6,
      foe: 'knock',
      dist: 2.6,
      fx: 'palmBurst',
      sfx: 'palmFinisher',
      cam: 'shake',
    },
    { at: 4.95, pose: 'tjClose', hits: 0, share: 0, foe: 'fall' },
    { at: 5.5, pose: 'tjStance', hits: 0, share: 0, foe: 'recover' },
  ],
});

/**
 * 迴 หมอกวนครอบฟ้า — soft fist: yield, then crush. The caster never leaves the ground and barely leaves the spot (the opposite
 * of พายุป่าไผ่, which lifts and flies): a turning mist disc draws the foe in, the caster's palms stick and swing it round twice on
 * its own force, turn that force back down (kneel), a palm barrage inside a rising mist dome, the dome sucked into the palms
 * (quiet), then one rooted double push: the pressure wave bursts the dome and the foe goes flat. Field opener r 3.2, tell 0.8 s,
 * fall at 3.8 s (4.6 s in all) + 1.0 s release. Rhythm: slow, slow, snap, flurry, breath, crush. Foe stays on the ground.
 */
export const MIST_DOME = registerLongMove({
  skillId: 'tf-supreme',
  kind: 'ult',
  name: 'หมอกวนครอบฟ้า',
  opener: {
    type: 'field',
    range: 0,
    radius: 3.2,
    tell: 0.8,
    tellPose: 'tgOpen',
    tellFx: 'tgMistRing',
    tellSfx: 'tgRageFlare',
  },
  // the foe is down from tgSettle (3.8 s); the caster holds its breath to 4.8 s
  len: 4.8,
  power: 3.8,
  lunge: 0,
  whiff: { pose: 'tgSpent', fx: 'tgMistScatter', sfx: 'tgSpent', stop: 0.9, rageBack: 10 },
  beats: [
    {
      at: 0,
      pose: 'tgDraw',
      hits: 1,
      share: 0.05,
      foe: 'pull',
      fx: 'tgMistDraw',
      sfx: 'tgDraw',
      cam: 'push',
      stop: 0.05,
    },
    {
      at: 0.5,
      pose: 'tgYieldL',
      hits: 1,
      share: 0.05,
      foe: 'spin',
      ai: 2,
      fx: 'tgMistArcL',
      sfx: 'tgYield1',
      cam: 'orbit',
      stop: 0.04,
    },
    {
      at: 0.95,
      pose: 'tgYieldR',
      hits: 1,
      share: 0.05,
      foe: 'spin',
      ai: 2,
      fx: 'tgMistArcR',
      sfx: 'tgYield2',
      cam: 'orbit',
      stop: 0.04,
    },
    {
      at: 1.4,
      pose: 'tgTurnBack',
      hits: 1,
      share: 0.09,
      foe: 'kneel',
      fx: 'tgMistFold',
      sfx: 'tgTurnBack',
      cam: 'hold',
      stop: 0.1,
    },
    {
      at: 1.85,
      pose: 'tgPalmRain',
      hits: 8,
      share: 0.22,
      foe: 'hit',
      ai: 4,
      fx: 'tgPalmRain',
      sfx: 'tgPalmRoll',
      cam: 'orbit',
      stop: 0.025,
    },
    {
      at: 2.75,
      pose: 'tgRoot',
      hits: 0,
      share: 0,
      foe: 'hit',
      fx: 'tgDomeGather',
      sfx: 'tgInhale',
      cam: 'pull',
    },
    {
      at: 3.35,
      pose: 'tgCrush',
      hits: 1,
      share: 0.54,
      foe: 'knock',
      dist: 2.4,
      ai: 2,
      radius: 4.0,
      fx: 'tgPressureWave',
      sfx: 'tgFinisher',
      cam: 'shake',
      stop: 0.18,
    },
    { at: 3.8, pose: 'tgSettle', hits: 0, share: 0, foe: 'fall', fx: 'tgMistSettle', sfx: 'tgMistSettle' },
    { at: 4.3, pose: 'tgStance', hits: 0, share: 0, foe: 'recover', fx: 'tgRingFade', sfx: 'tgExhale' },
  ],
});
